<template>
  <View direction="column" gap="sm">
    <!-- Divs, since the description brings paragraphs and lists of its own -->
    <Text variant="body-medium" as="div" class="text">
      <div v-html="displayedText" />
    </Text>
    <View v-if="isTextTooLong">
      <Button variant="ghost" size="small" @click="toggleText">
        {{ showFullText ? 'See Less ↑' : 'See More ↓' }}
      </Button>
    </View>
  </View>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';

import { Button, Text, View } from '@tallo/design-system';
import { sanitizeDescription } from '@tallo/utility';

import { PropertyDto } from '../../model/property';

const props = defineProps<{
  property: PropertyDto | null | undefined;
}>();

const maxTextLength = 250;
const textLengthWithoutEllipsis = maxTextLength - 6;
const showFullText = ref(true);
const description = computed(() => props.property?.description || '');
const isTextTooLong = computed(() => description.value.length > maxTextLength);
const displayedText = computed(() => sanitizeDescription(visibleText.value));
const visibleText = computed(() => {
  if (showFullText.value || !isTextTooLong.value) {
    return description.value;
  } else {
    // Trim the description to a length that accommodates the ellipsis
    let trimmedText = description.value.substring(0, textLengthWithoutEllipsis);

    // Find the last space to avoid cutting in the middle of a word
    let lastSpaceIndex = trimmedText.lastIndexOf(' ');

    // If there's no space (a very long word), trim the text directly
    if (lastSpaceIndex === -1) {
      lastSpaceIndex = textLengthWithoutEllipsis;
    }

    // Replace the end of the text with an ellipsis
    return trimmedText.substring(0, lastSpaceIndex) + '...';
  }
});

const toggleText = () => {
  showFullText.value = !showFullText.value;
};
</script>

<style scoped lang="scss">
// Owners type descriptions with blank lines between paragraphs; they read the way they were written
.text {
  white-space: break-spaces;
}
</style>
